import React, { useState, useMemo, useEffect } from 'react';
import { 
  CheckCircle, 
  FolderOpen, 
  Tag, 
  FileText,
  DollarSign,
  Clock,
  Calculator,
  Info
} from "lucide-react";
import axios from '../../services/axios';
import { message } from 'antd';

const MoneyInput = ({ label, value, onChange, icon: Icon, currency }) => (
  <div className="group hover:bg-blue-50/30 transition-all duration-200 rounded-xl p-4 -mx-4">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="w-5 h-5 text-blue-500" />}
        <span className="text-gray-700 font-medium text-sm sm:text-base">{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-gray-400 font-semibold text-sm">{currency}</span>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          placeholder="0.00"
          onChange={(e) => {
            const val = e.target.value;

            if (val === "" || /^[0-9]*\.?[0-9]*$/.test(val)) {
              onChange(val);
            }
          }}
          className="w-full sm:w-40 px-4 py-2.5 border-2 border-gray-200 rounded-xl text-right 
                      focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all
                      bg-white hover:border-gray-300 text-gray-800 font-medium"
        />
      </div>
    </div>
  </div>
);

// const initialData = {
//   preliminaries: "",
//   workDone: "",
//   provisionalSums: "",
//   vatPercent: 5,
//   ipaSubmission: "25th of every month",
//   ipcIssuance: 28,
//   paymentReceipt: 28,
// };

const ReadOnlyMoney = ({ label, value, highlight = false, currency }) => (
  <div
    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl
      ${highlight ? 'bg-gradient-to-r from-blue-50 to-indigo-50' : ''}`}
  >
    <span className={`font-semibold ${highlight ? 'text-gray-800 text-lg' : 'text-gray-700'}`}>
      {label}
    </span>
    <span
      className={`font-bold ${
        highlight ? 'text-2xl text-blue-600' : 'text-lg text-emerald-600'
      }`}
    >
      {currency}{" "}
      {value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
    </span>
  </div>
);

const InfoCard = ({ icon: Icon, title, children }) => (
  <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-2xl p-6 shadow-sm border border-blue-100">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 bg-blue-100 rounded-lg">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <h3 className="font-semibold text-gray-800">{title}</h3>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const TimelineInput = ({ label, value, onChange, type = "text", suffix = "" }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
    <span className="font-medium text-gray-700 text-sm sm:text-base">{label}</span>

    <div className="flex items-center gap-2">
      <input
        type={type}
        value={value}
        onChange={(e) => {
          const val = e.target.value;
          if (type === "number") {
            if (/^\d*$/.test(val)) onChange(val);
          } else {
            onChange(val);
          }
        }}
        className="px-4 py-2 border-2 border-gray-200 rounded-lg
          focus:ring-2 focus:ring-blue-400 focus:border-blue-400
          bg-white hover:border-gray-300 text-gray-800"
      />
      {suffix && <span className="text-gray-500 font-medium">{suffix}</span>}
    </div>
  </div>
);

const initialData = {
  preliminaries: "",
  workDone: "",
  provisionalSums: "",
  vatPercent: "5",
  ipaSubmission: "25th of every month",
  ipcIssuance: "28",
  paymentReceipt: "28",
};

const CommercialSetting = () => {
  const [data, setData] = useState(initialData);
  const [currency, setCurrency] = useState('');
  const [loading, setLoading] = useState(false);
  const user = localStorage.getItem('user');

  const toNumber = (val) => Number(val || 0);

  const calculated = useMemo(() => {
    const total =
      toNumber(data.preliminaries) +
      toNumber(data.workDone) +
      toNumber(data.provisionalSums);

    const vatAmount = (total * toNumber(data.vatPercent)) / 100;
    const grandTotal = total + vatAmount;

    return { total, vatAmount, grandTotal };
  }, [
    data.preliminaries,
    data.workDone,
    data.provisionalSums,
    data.vatPercent
  ]);

  useEffect(() => {
    const fetchCurrency = async () => {
      setLoading(true);
      try {
        const project = JSON.parse(localStorage.getItem('Project'))    
        const res = await axios.post('/fetch-project', {
          projectName: project
        });
        setCurrency(res.data.project.currency);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        if (err.response && err.response.data && err.response.data.msg) {
          message.warning(err.response.data.msg);
        } else if (err.response && err.response.data && err.response.data.userName) {
          message.warning(err.response.data.userName[0]);
        } else if (err.response && err.response.status === 401) {
          message.error('User not authorized');
        } else {
          message.error('Something went wrong');
        }
      }
    };
    fetchCurrency();
  }, []);

  const handleSave = async () => {
    try {
        setLoading(true);

        const project = JSON.parse(localStorage.getItem('Project'));

        const payload = {
            user,
            projectName: project,
            preliminaries: Number(data.preliminaries),
            workDone: Number(data.workDone),
            provisionalSums: Number(data.provisionalSums),
            subtotal: calculated.total,
            vatPercent: Number(data.vatPercent),
            vatAmount: calculated.vatAmount,
            grandTotal: calculated.grandTotal,
            ipaSubmission: data.ipaSubmission,
            ipcIssuance: Number(data.ipcIssuance),
            paymentReceipt: Number(data.paymentReceipt),
            active: true
        };

        const res = await axios.post(
            '/save-commercialSettings',
            payload
        );

        message.success(res.data.message || "Commercial settings saved");
        setData(initialData);
    } catch (err) {
        setLoading(false);
        if (err.response && err.response.data && err.response.data.msg) {
            message.warning(err.response.data.msg);
        } else if (err.response && err.response.data && err.response.data.userName) {
            message.warning(err.response.data.userName[0]);
        } else if (err.response && err.response.status === 401) {
            message.error('User not authorized');
        } else {
            message.error('Something went wrong');
        }    
    } finally {
        setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30 py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="space-y-6">
          {/* Financial Inputs Card */}
          <InfoCard icon={DollarSign} title="Commercial Details">
            <MoneyInput
              label="Preliminaries"
              value={data.preliminaries}
              onChange={(v) => handleChange("preliminaries", v)}
              icon={FileText}
            />
            <MoneyInput
              label="Work Done"
              value={data.workDone}
              onChange={(v) => handleChange("workDone", v)}
              icon={CheckCircle}
            />
            <MoneyInput
              label="Provisional Sums"
              value={data.provisionalSums}
              onChange={(v) => handleChange("provisionalSums", v)}
              icon={FolderOpen}
            />
          </InfoCard>

          {/* Calculations Card */}
          <InfoCard icon={Calculator} title="Calculations">
            <ReadOnlyMoney
              label="Subtotal"
              value={calculated.total}
              currency={currency}
            />

            
            <div className="bg-white/50 rounded-xl p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-blue-500" />
                  <span className="font-medium text-gray-700">VAT Rate</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={data.vatPercent}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val)) {
                        handleChange("vatPercent", val);
                        }
                    }}
                    className="w-20 px-3 py-2 border-2 border-gray-200 rounded-lg text-center
                                focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all
                                bg-white hover:border-gray-300 font-medium"
                  />
                  <span className="text-gray-500 font-semibold">%</span>
                  <span className="ml-0 sm:ml-4 text-emerald-600 font-bold text-lg">
                    {currency} {calculated.vatAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <ReadOnlyMoney label="Grand Total" value={calculated.grandTotal} highlight={true} />
            </div>

            <div className="flex items-start gap-2 mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-700">
                Grand Total = Subtotal + VAT Amount
              </p>
            </div>
          </InfoCard>

          {/* Timeline Settings Card */}
          <InfoCard icon={Clock} title="Timeline Configuration">
            <TimelineInput
              label="IPA Submission"
              value={data.ipaSubmission}
              onChange={(v) => handleChange("ipaSubmission", v)}
              type="text"
            />
            <TimelineInput
              label="IPC Issuance"
              value={data.ipcIssuance}
              onChange={(v) => handleChange("ipcIssuance", v)}
              type="number"
              suffix="days"
            />
            <TimelineInput
              label="Payment Receipt"
              value={data.paymentReceipt}
              onChange={(v) => handleChange("paymentReceipt", v)}
              type="number"
              suffix="days"
            />
          </InfoCard>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
                onClick={handleSave}
                disabled={loading}
                className={`flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold 
                    py-3 px-6 rounded-xl hover:from-blue-600 hover:to-indigo-700 
                    transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl
                    ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
                {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommercialSetting;